import { useQuery, type QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'
import { analyticsEnabledQuery } from '@/lib/queries/app-settings'
import { isAdministratorQuery } from '@/lib/queries/admin'
import { nodes } from '@/lib/queries/mappers'
import { currentUser } from '@/lib/auth/session'
import { orgRoutes } from '@/lib/routes'

/**
 * Does the current user reach the analytics wave (dashboard, saved views,
 * identities, the per-code scans ledger)?
 *
 * Yes when the `analytics_enabled` flag is on for everyone, OR when the user is
 * a platform administrator — the same two-part rule the server enforces in
 * `withAnalyticsScopedRunner`, so the nav a user sees and the endpoints they can
 * reach never disagree. The flag alone is the release switch; the administrator
 * clause is what lets the feature be exercised in prod while it is still off.
 *
 * This is only the UX wall — hiding what a user cannot use. The endpoints are
 * the real boundary.
 */
export function useAnalyticsAccess(userId: string): boolean {
  const { data: enabled = false } = useQuery(analyticsEnabledQuery())
  const { data: isAdmin = false } = useQuery(isAdministratorQuery(userId))
  return enabled || isAdmin
}

/**
 * The same answer inside a route `beforeLoad`, where hooks cannot run. Shares
 * the two queries' cache with `useAnalyticsAccess`, so a guarded route that
 * lets you through has already warmed what the sidebar reads.
 *
 * `ensureQueryData` returns the raw server shape — it has no `select` to apply,
 * unlike the `useQuery` observers — so the predicates run here on data of a
 * known shape. The options are spread into a variable first: `ensureQueryData`
 * ignores the leftover `select` field, but an object literal carrying it would
 * trip the excess-property check.
 */
export async function ensureAnalyticsAccess(
  queryClient: QueryClient,
  userId: string,
): Promise<boolean> {
  const enabledOptions = { ...analyticsEnabledQuery() }
  const adminOptions = { ...isAdministratorQuery(userId) }
  const [enabledData, adminData] = await Promise.all([
    queryClient.ensureQueryData(enabledOptions),
    queryClient.ensureQueryData(adminOptions),
  ])
  const enabled = enabledData.value === 'true'
  const isAdmin = nodes(adminData.administratorsCollection).length > 0
  return enabled || isAdmin
}

/**
 * `beforeLoad` guard for the walled analytics routes: bounce a user who cannot
 * reach the feature back to their QR codes, so a direct URL (or a stale
 * bookmark from when the flag was on) never lands on a screen whose every
 * request would 403.
 *
 * A missing session is left to the `/o/$org` loader, which owns that redirect —
 * guarding it here too would only race it.
 */
export async function requireAnalyticsAccess({
  queryClient,
  orgSlug,
}: {
  queryClient: QueryClient
  orgSlug: string
}): Promise<void> {
  const user = await currentUser()
  if (!user) return
  const allowed = await ensureAnalyticsAccess(queryClient, user.id)
  if (!allowed) throw redirect({ ...orgRoutes(orgSlug).qrCodes })
}
